// «Машины» — рабочий парк диспетчера (встреча 15.10): машины, которые сейчас работают у нас (свои и наёмные),
// и где каждая: зелёный — на заводе, синий — едет на объект, красный — на объекте, оранжевый — едет на завод.
// Машину на рейс диспетчер выбирает сама; здесь — кто есть и кто где.

import { useState } from "react";
import { fmtPhone, fmtTime, telHref } from "../../shared/format";
import { useLive } from "../../shared/live";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Empty, Input, Loading, Modal, Plate, useAsync, useToast } from "../../shared/ui";
import { api, can, live, TRIP_STATUS, useCtx } from "../ctx";

export const PLACE: Record<string, { label: string; cls: string }> = {
  at_plant: { label: "на заводе", cls: "pl-plant" },
  to_site: { label: "едет на объект", cls: "pl-tosite" },
  on_site: { label: "на объекте", cls: "pl-site" },
  to_plant: { label: "едет на завод", cls: "pl-toplant" },
};

export function PlaceDot({ place }: { place: string | null | undefined }) {
  const p = place ? PLACE[place] : null;
  return <span className={`pl-dot ${p?.cls || "pl-none"}`} title={p?.label || "неизвестно где"} aria-label={p?.label || "неизвестно где"} />;
}

type Brief = { trip_id: string; order_id: string; order_number: number; seq: number; status: string; site: string; customer: string; load_start: string; plant: string };
type Row = {
  id: string; plate: string; drum_m3: number; owner: string; carrier: string | null; place: string | null; place_at: string | null;
  driver: { name: string; phone: string | null } | null; current: Brief | null; next: Brief | null; trips_today: number;
};

const ORDER = ["at_plant", "to_plant", "to_site", "on_site", "unknown"];

export function FleetPage() {
  const { me } = useCtx();
  const toast = useToast();
  const board = useAsync<{ vehicles: Row[]; counts: Record<string, number> }>(() => api.get("/fleet/board"), []);
  useLive(live, ["trip.", "vehicle."], () => board.reload(), 500);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const dispatch = can.dispatch(me.role);
  const d = board.data;

  const setPlace = async (v: Row, place: string) => {
    try { await api.post(`/fleet/vehicles/${v.id}/place`, { place }); board.reload(); } catch (e) { toast.error(e); }
  };
  const offDuty = async (v: Row) => {
    try { await api.patch(`/vehicles/${v.id}`, { on_duty: false }); toast.ok(`${v.plate} убрана из рабочего парка`); board.reload(); } catch (e) { toast.error(e); }
  };

  const needle = q.trim().toLowerCase().replace(/\s+/g, "");
  const rows = (d?.vehicles || [])
    .filter((v) => !only || (v.place || "unknown") === only)
    .filter((v) => !needle || `${v.plate}${v.driver?.name || ""}${v.carrier || ""}`.toLowerCase().replace(/\s+/g, "").includes(needle))
    .sort((a, b) => ORDER.indexOf(a.place || "unknown") - ORDER.indexOf(b.place || "unknown") || a.plate.localeCompare(b.plate));

  return (
    <>
      <TopBar title="Машины">
        {dispatch && <Button variant="primary" onClick={() => setAdding(true)}>Добавить машину в работу</Button>}
      </TopBar>
      <div className="content stack">
        {!d ? <Loading error={board.error} onRetry={board.reload} /> : (
          <>
            <div className="row" role="group" aria-label="Где машины">
              <Button size="small" variant={only === null ? "primary" : undefined} onClick={() => setOnly(null)}>Все: {d.vehicles.length}</Button>
              {ORDER.map((k) => (
                <Button key={k} size="small" variant={only === k ? "primary" : undefined} onClick={() => setOnly(only === k ? null : k)}>
                  <PlaceDot place={k === "unknown" ? null : k} />{k === "unknown" ? "неизвестно" : PLACE[k].label}: {d.counts[k] || 0}
                </Button>
              ))}
              <div className="grow" />
              <Input placeholder="Госномер, водитель, перевозчик" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} aria-label="Поиск машины" />
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Где машина — по статусам рейсов (отметки оператора, водителя и диспетчера). Без рейса водитель нажимает в приложении «Я на заводе».
              Если отметки нет — исправьте вручную кнопками в строке.
            </p>
            {rows.length === 0 ? <Empty>Машин нет. Добавьте машины, которые сейчас работают у вас.</Empty> : (
              <div className="panel table-wrap">
                <table className="table">
                  <thead><tr><th>Где</th><th>Машина</th><th>Водитель</th><th>Сейчас</th><th>Следующий рейс</th><th className="num">Рейсов сегодня</th>{dispatch && <th />}</tr></thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.id}>
                        <td><span className={`pl-tag ${v.place ? PLACE[v.place]?.cls : "pl-none"}`}>{v.place ? PLACE[v.place]?.label : "неизвестно"}</span>
                          {v.place_at && <div className="small muted">с {fmtTime(v.place_at)}</div>}</td>
                        <td><Plate value={v.plate} small /><div className="small muted">{v.drum_m3} м³ · {v.owner === "own" ? "свой" : `наёмный${v.carrier ? `, ${v.carrier}` : ""}`}</div></td>
                        <td>{v.driver ? <>{v.driver.name}{v.driver.phone && <div className="small"><a href={telHref(v.driver.phone)}>{fmtPhone(v.driver.phone)}</a></div>}</> : <span className="muted">—</span>}</td>
                        <td>{v.current ? <TripLink t={v.current} /> : <span className="muted small">без рейса</span>}</td>
                        <td>{v.next ? <TripLink t={v.next} withTime /> : <span className="muted small">—</span>}</td>
                        <td className="num">{v.trips_today}</td>
                        {dispatch && (
                          <td className="right">
                            <div className="row tight" style={{ justifyContent: "flex-end" }}>
                              {Object.entries(PLACE).filter(([k]) => k !== v.place).map(([k, p]) => (
                                <Button key={k} size="small" variant="ghost" title={`Отметить: ${p.label}`} onClick={() => setPlace(v, k)}><PlaceDot place={k} /></Button>
                              ))}
                              <Button size="small" variant="ghost" onClick={() => offDuty(v)}>Убрать из работы</Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      {adding && <AddToDuty onClose={() => setAdding(false)} onDone={() => { setAdding(false); board.reload(); }} />}
    </>
  );
}

function TripLink({ t, withTime }: { t: Brief; withTime?: boolean }) {
  return (
    <button className="linkbtn" style={{ textAlign: "left" }} onClick={() => navigate("/schedule", { order: t.order_id })}>
      №{t.order_number}/{t.seq} · {t.site}
      <div className="small muted">{withTime ? `погрузка ${fmtTime(t.load_start)}, ${t.plant}` : TRIP_STATUS[t.status]}</div>
    </button>
  );
}

/** Машины, которые не в рабочем парке (на другом заводе, ремонт закончился): вернуть в работу. */
function AddToDuty({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const list = useAsync<any>(() => api.get("/vehicles", { limit: 1000 }), []);
  const needle = q.trim().toLowerCase().replace(/\s+/g, "");
  const rows = (list.data?.items || []).filter((v: any) => !v.on_duty && (!needle || `${v.plate}${v.driver_name || ""}`.toLowerCase().replace(/\s+/g, "").includes(needle)));
  return (
    <Modal title="Добавить машину в работу" onClose={onClose} actions={<Button onClick={onClose}>Закрыть</Button>}>
      <div className="stack tight">
        <Input autoFocus placeholder="Госномер или водитель" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск машины" />
        {!list.data ? <Loading error={list.error} /> : rows.length === 0 ? <p className="muted small">Все машины уже в работе. Новую машину заведите в «Справочники» → «Транспорт».</p> : (
          <div className="stack tight" style={{ maxHeight: 360, overflow: "auto" }}>
            {rows.map((v: any) => (
              <div key={v.id} className="row">
                <b className="num">{v.plate}</b>
                <span className="small grow">{v.drum_m3} м³, {v.owner === "own" ? "свой" : "наёмный"}{v.driver_name ? `, ${v.driver_name}` : ""}</span>
                <Button size="small" onClick={async () => {
                  try { await api.patch(`/vehicles/${v.id}`, { on_duty: true }); toast.ok(`${v.plate} в работе`); onDone(); } catch (e) { toast.error(e); }
                }}>В работу</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
